import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { getFieldPractices, getFieldPracticesAdmin } from "services";

import { getEnvironment } from "utils";

import { GET_FIELD_PRACTICES } from "consts";

import { FieldPracticeResponse } from "models";

type QueryFnParams = {
  fieldId: string;
  isAdmin?: boolean;
};

const env = getEnvironment();

export const useGetFieldPractices = ({
  fieldId,
  inView,
  isAdmin = env.admin,
}: QueryFnParams & {
  inView: boolean;
}) => {
  // Not having { useSuspense: false } caused unintended UI behavior — like a drawer opening and closing on mount,
  // due to remounting triggered by Suspense fallback.
  // DO NOT REMOVE unless you're explicitly handling Suspense with fallback UIs.
  const { t } = useTranslation(["practices"], { useSuspense: false });
  const withCropNames = useCropNames(t);

  return useQuery<FieldPracticeResponse>({
    queryKey: [GET_FIELD_PRACTICES, fieldId, isAdmin],
    queryFn: () => fetchFieldPractices({ fieldId, isAdmin }),
    select: withCropNames,
    enabled: inView && !!fieldId,
    staleTime: 1000 * 60 * 60 * (isAdmin ? 2 : 24), // 1 day for user application, 2 hours for admin
  });
};

// Shared by every query on [GET_FIELD_PRACTICES, fieldId, isAdmin], so the cached shape is always the same.
export const fetchFieldPractices = ({ fieldId, isAdmin }: QueryFnParams): Promise<FieldPracticeResponse> => {
  if (isAdmin) {
    return getFieldPracticesAdmin({ fieldId });
  }

  return getFieldPractices({ fieldId });
};

// Names go in at select, not in the fetch, so the cache stays language-neutral and the names follow a language change.
export const useCropNames = (t: (key: string) => string) =>
  useCallback(
    (fieldPracticeData: FieldPracticeResponse): FieldPracticeResponse => ({
      ...fieldPracticeData,
      // Why is it `cropName` here and not `crop.name` like everywehre else :D
      crops: fieldPracticeData.crops.map((crop) => ({ ...crop, cropName: t(`crops.${crop.translationSlug}`) })),
    }),
    [t]
  );
